import { RouteRiskSegment } from '../types';

/** Shares the congestion palette so the two route colourings read alike. */
export const RISK_COLORS = {
  quiet: '#4c8bf5',
  low: '#e8a33d',
  elevated: '#e0563d',
  high: '#a8261c',
};

/** Matches the backend's HOTSPOT_FLOOR: below this a segment is noise. */
export const RISK_FLOOR = 12;

export function riskColor(score: number): string {
  if (score < RISK_FLOOR) return RISK_COLORS.quiet;
  if (score < 35) return RISK_COLORS.low;
  if (score < 60) return RISK_COLORS.elevated;
  return RISK_COLORS.high;
}

export function riskLabel(score: number): string {
  if (score < RISK_FLOOR) return 'Quiet';
  if (score < 35) return 'Low';
  if (score < 60) return 'Elevated';
  return 'High';
}

/**
 * Build a `line-gradient` from scored segments.
 *
 * Segments are already distance-bounded, so they map straight onto
 * `line-progress` without walking the geometry again. Stops are emitted only
 * where the colour changes, keeping the expression short on long routes.
 */
export function buildRiskGradient(
  segments: RouteRiskSegment[],
  totalDistance: number,
): unknown[] | null {
  if (!segments.length || totalDistance <= 0) return null;
  if (segments.every((s) => s.score < RISK_FLOOR)) return null;

  const expression: unknown[] = ['interpolate', ['linear'], ['line-progress']];
  let lastColor: string | null = null;
  let lastStop = -1;

  for (const segment of segments) {
    const color = riskColor(segment.score);
    if (color === lastColor) continue;
    const at = Math.min(1, Math.max(0, segment.start_distance / totalDistance));
    // Mapbox rejects non-ascending stops, so never step backwards.
    if (at <= lastStop) continue;
    // A hard colour change needs two stops at (almost) the same offset.
    if (lastColor !== null) {
      const before = Math.max(lastStop + 1e-7, at - 1e-6);
      if (before > lastStop && before < at) {
        expression.push(before, lastColor);
        lastStop = before;
      }
    }
    expression.push(at, color);
    lastStop = at;
    lastColor = color;
  }
  if (lastStop < 1) expression.push(1, lastColor ?? RISK_COLORS.quiet);

  return expression.length >= 5 ? expression : null;
}
